import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Button,
  FormHeading,
  FormGroup,
  Divider,
  Toggle,
  TextInput,
  Label
} from '@ubisend/pulse-components';
import { Rows } from '@ubisend/pulse-hooks';
import { useAuth, hasActiveFeatureGate } from '@ubisend/pulse-auth';
import TemplatingCodeEditor, { tags } from '@ubisend/pulse-templating';

import Request from './Request';
import AttributeInput from './AttributeInput';

const defaultJsonBody = `{

}`;

const defaultXmlBody = `<?xml version="1.0" encoding="utf-8"?>
  <soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
    <soap:Body>

    </soap:Body>
</soap:Envelope>`;

const EndpointDetails = ({
  handleSubmit,
  request,
  parameters,
  initialBody = '',
  initialName = '',
  initialUseBody = false,
  resetName
}) => {
  const auth = useAuth();
  const isTemplatingEnabled = hasActiveFeatureGate('templating')(auth);

  const determineBody = () => {
    if (initialBody && initialBody.length > 0) {
      // if user-defined body is present use that
      return initialBody;
    }
    // otherwise return default json or xml based on rest vs soap
    return request.isSoap ? defaultXmlBody : defaultJsonBody;
  };

  const [name, setName] = useState(initialName);
  const [useBody, setUseBody] = useState(initialUseBody);
  const [body, setBody] = useState(determineBody);

  useEffect(() => {
    // Reset name when resetName prop changes
    setName(initialName);
  }, [initialName, resetName]);

  const valid = useMemo(() => {
    const passes = request.valid;

    if (!passes) {
      return false;
    }

    return useBody ? Boolean(body) : parameters.valid;
  }, [request.valid, useBody, body, parameters.valid]);

  const handleNameChange = event => {
    setName(event.target.value);
  };

  const handleBodyChange = body => {
    setBody(body);
  };

  const onSubmit = event => {
    event.preventDefault();

    if (!valid) {
      return;
    }

    if (useBody) {
      return handleSubmit({ ...request.form, name, body });
    }

    handleSubmit({ ...request.form, name, parameters: parameters.rows });
  };

  return (
    <form onSubmit={onSubmit}>
      <FormHeading>Request</FormHeading>
      <FormGroup>
        <Label htmlFor="name">Name</Label>
        <TextInput
          id="name"
          placeholder="Get users"
          value={name}
          onChange={handleNameChange}
        />
      </FormGroup>
      <Request {...request} />
      <Divider />
      <Flex between top>
        <FormHeading>Request Body</FormHeading>
        <Toggle
          neutral
          options={['Params', request.isSoap ? 'XML' : 'JSON']}
          on={useBody}
          handleToggleChange={() => setUseBody(!useBody)}
        />
      </Flex>
      <FormGroup>
        {useBody ? (
          <TemplatingCodeEditor
            aria-label={request.isSoap ? 'Body xml' : 'Body json'}
            value={body}
            isSoap={request.isSoap}
            onChange={handleBodyChange}
            tags={
              request.isSoap
                ? []
                : isTemplatingEnabled
                ? [tags.if, tags.for]
                : []
            }
          />
        ) : (
          <Rows {...parameters} KeyInput={AttributeInput} />
        )}
      </FormGroup>
      <Flex right>
        <Button variant="primary" disabled={!valid} type="submit">
          Save
        </Button>
      </Flex>
    </form>
  );
};

EndpointDetails.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  request: PropTypes.object,
  parameters: PropTypes.object,
  initialSoapFunction: PropTypes.string,
  initialBody: PropTypes.string,
  initialName: PropTypes.string,
  initialUseBody: PropTypes.bool,
  resetName: PropTypes.bool
};

export default EndpointDetails;
